import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input, model } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent } from '../button';
import { InputComponent } from '../form';
import { TextEllipsisComponent } from '../text-ellipsis';
import { MbsSize } from '../utils';

@Component({
  selector: 'mbs-list-editor',
  templateUrl: './list-editor.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ListEditorComponent)
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, I18NextPipe, InputComponent, ReactiveFormsModule, TextEllipsisComponent],
  standalone: true
})
export class ListEditorComponent implements ControlValueAccessor {
  private readonly i18n = inject(I18NextPipe);

  protected readonly MbsSize = MbsSize;
  protected readonly errorMessage = {
    unique: { message: this.i18n.transform('mbs-ui-kit:list-editor.error.unique', { format: 'capitalize' }) }
  } as ValidationErrors;

  /**
   * Source data
   */
  public readonly data = model<string[]>();

  /**
   * Input validation function @type ValidatorFn
   */
  public readonly validators = input<ValidatorFn[]>([]);

  /**
   * Input type
   */
  public readonly inputType = input<'text' | 'password'>('text');

  /**
   * Identifier DOM element for mapping label and input
   */
  public readonly id = input<string>('list-editor');

  /**
   * Append entity name
   */
  public readonly entityName = input<string>('');

  /**
   * Show label control
   */
  public readonly showLabel = input<boolean>(true);

  protected readonly copyData = computed<Set<string>>(() => new Set(this.data()));
  protected readonly inputId = computed<string>(() => this.id() + '-input-id');
  protected readonly label = computed<string>(() =>
    this.i18n.transform('mbs-ui-kit:list-editor.label', {
      format: 'capitalize',
      name: this.entityName()?.toLowerCase()
    })
  );
  protected readonly placeholder = computed<string>(() =>
    this.i18n.transform('mbs-ui-kit:list-editor.placeholder', {
      format: 'capitalize',
      name: this.entityName()?.toLowerCase()
    })
  );

  /**
   * Current value in FormControl
   * @ignore
   */
  protected readonly formControl = computed(() => {
    const uniqueValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
      this.copyData()?.has(control.value) ? this.errorMessage : null;

    return new FormControl('', [...this.validators(), Validators.required, uniqueValidator]);
  });

  /**
   * Checking valid
   * @return {boolean}
   */
  get isFormCtrlValid(): boolean {
    const { dirty, invalid, touched, value } = this.formControl();

    return value.length === 0 || (invalid && (dirty || touched));
  }

  /**
   * Append new value to the list
   */
  handleAddValue() {
    if (this.isFormCtrlValid) return;

    const set = this.copyData();
    const { value } = this.formControl();

    set.add(value);

    this.handleReset();
    this.notifyValueChange([...set]);
  }

  /**
   * Delete item by index
   * @param {any} item
   */
  handleDelete(item: any): void {
    const set = this.copyData();

    set.delete(item);

    this.notifyValueChange([...set]);
  }

  /**
   * Reset input after clear or add
   */
  handleReset(): void {
    this.formControl().reset('');
  }

  /**
   * ControlValueAccessor methods
   * @ignore
   */
  onChange: (value: string[]) => void;
  onTouched: () => void;

  writeValue(obj: Array<any>): void {
    const data = obj ? Array.from(obj) : [];

    this.data.set(data);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private notifyValueChange(data: string[]): void {
    this.onChange?.(data);
    this.onTouched?.();
  }
}
